import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Convert an ISO string to the value shape a <input type="datetime-local"> expects
// (i.e. "YYYY-MM-DDTHH:MM" in the user's LOCAL time zone).
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DateEditor({ value, onSave, testIdPrefix = "date-editor" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setDraft(isoToLocalInput(value)); }, [open, value]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const iso = new Date(draft).toISOString();
      await onSave(iso);
      setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-open`}
          title="Edit date (Super Admin only)"
          className="ml-1 p-0.5 text-zinc-600 hover:text-amber-400 align-middle"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="bg-[#0a0a0a] border border-amber-500/40 rounded-sm p-3 w-64"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testIdPrefix}-popover`}
      >
        <div className="text-[10px] font-mono uppercase tracking-wider text-amber-400 mb-2">Adjust Date</div>
        <Input
          type="datetime-local"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid={`${testIdPrefix}-input`}
          className="bg-zinc-950 border-zinc-800 rounded-sm h-8 font-mono text-xs"
        />
        <div className="flex justify-end gap-1 mt-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-7 rounded-sm border-zinc-800 bg-transparent text-zinc-400">
            <X className="w-3 h-3" />
          </Button>
          <Button size="sm" onClick={save} disabled={busy || !draft} data-testid={`${testIdPrefix}-save`} className="h-7 rounded-sm bg-amber-600 hover:bg-amber-500 text-black gap-1">
            <Check className="w-3 h-3" /> Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
